import React, { useState, useEffect } from 'react';
import { websocketManager } from '../../lib/WebSocketManager';
import { getSafePostLoginRedirect } from '../../lib/loginRedirect';
import SocialLoginButtons from '../social-login-buttons';
import { useTranslation } from '../../i18n/useTranslation';
import '../../style/login.css';

const PopupLogin = ({ display, onClose, onLoginSuccess, initialMode = 'login' }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(initialMode);

  // initialMode가 바뀔 때(팝업 열릴 때) mode 동기화
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  // --- 로그인 상태 ---
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');

  // --- 회원가입 상태 ---
  const [signupNickname, setSignupNickname] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const resetForms = () => {
    setIdentifier(''); setPassword('');
    setSignupNickname(''); setSignupEmail('');
    setSignupPassword(''); setSignupConfirm('');
    setMessage('');
  };

  const switchMode = (next) => {
    resetForms();
    setMode(next);
  };

  // ── 로그인 ──────────────────────────────────────
  const handleLogin = async () => {
    setMessage('');
    if (!identifier || !password) {
      setMessage(t('login.errorRequired'));
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/login-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password }),
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.require2FA) {
        sessionStorage.setItem('pre_auth_token', data.pre_auth_token);
        onClose();
        window.location.href = '/2fa-verify';
        return;
      }
      if (response.ok && data.token) {
        localStorage.setItem('auth_token', data.token);
        if (data.email) localStorage.setItem('email', data.email);
        if (data.nickname) localStorage.setItem('nickname', data.nickname);
        if (data.id) localStorage.setItem('id', data.id);
        if (data.membership) localStorage.setItem('membership', data.membership);
        if (data.profile_image) localStorage.setItem('profile_image', data.profile_image);
        websocketManager.subscribe();
        window.dispatchEvent(new Event('userDataChanged'));
        setMessage(t('login.success'));
        const redirectPath = getSafePostLoginRedirect(sessionStorage.getItem('redirectAfterLogin'));
        sessionStorage.removeItem('redirectAfterLogin');
        setTimeout(() => {
          onLoginSuccess?.({
            auth: data,
            redirectPath,
          });
          onClose();
          window.location.assign(redirectPath);
        }, 500);
      } else {
        setMessage(data.message || t('login.errorFailed'));
      }
    } catch (e) {
      console.error('Login error:', e);
      setMessage(t('login.errorNetwork'));
    } finally {
      setLoading(false);
    }
  };

  // ── 회원가입 ────────────────────────────────────
  const handleSignup = async () => {
    setMessage('');
    if (!signupNickname || !signupEmail || !signupPassword || !signupConfirm) {
      setMessage(t('signup.errorRequired'));
      return;
    }
    if (signupPassword !== signupConfirm) {
      setMessage(t('signup.errorPasswordMismatch'));
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nickname: signupNickname,
          email: signupEmail,
          password: signupPassword,
        }),
        credentials: 'include',
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        setMessage(t('login.errorNetwork'));
        return;
      }
      const data = await response.json();
      if (response.ok) {
        setMessage(t('signup.success'));
        setTimeout(() => switchMode('login'), 1200);
      } else {
        setMessage(data.message || t('signup.errorFailed'));
      }
    } catch (e) {
      console.error('Signup error:', e);
      setMessage(t('login.errorNetwork'));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLoginError = (provider, error) => {
    setMessage(provider === 'kakao'
      ? t('login.errorKakao')
      : (error?.message || t('login.errorGoogle')));
  };

  const handleSocialLoginSuccess = (provider, auth) => {
    if (provider !== 'google') return;
    websocketManager.subscribe();
    setMessage(t('login.success'));
    const redirectPath = getSafePostLoginRedirect(sessionStorage.getItem('redirectAfterLogin'));
    sessionStorage.removeItem('redirectAfterLogin');
    setTimeout(() => {
      onLoginSuccess?.({ auth, redirectPath });
      onClose();
      window.location.assign(redirectPath);
    }, 500);
  };

  const handleContentClick = (e) => e.stopPropagation();

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      mode === 'login' ? handleLogin() : handleSignup();
    }
  };

  // ── 렌더 ────────────────────────────────────────
  const isLogin = mode === 'login';

  return (
    <div
      className="popup-overlay popup-login-overlay"
      style={{ display }}
      onClick={onClose}
    >
      <div className="popup-panel popup-login-container" onClick={handleContentClick}>
        <div className="popup-login-content">

          {/* 로고 */}
          <div className="popup-login-logo">
            <h1>ASTRANTIS</h1>
          </div>

          {/* 헤더 */}
          <div className="popup-login-header">
            <h2 className="popup-login-title">
              {isLogin ? t('login.loginButton') : t('signup.title')}
            </h2>
            <p className="popup-login-subtitle">
              {isLogin ? (
                <>
                  {t('login.noAccount')}{' '}
                  <button
                    type="button"
                    className="popup-login-link popup-login-link-btn"
                    onClick={() => switchMode('signup')}
                  >
                    {t('login.createAccount')}
                  </button>
                </>
              ) : (
                <>
                  {t('signup.hasAccount')}{' '}
                  <button
                    type="button"
                    className="popup-login-link popup-login-link-btn"
                    onClick={() => switchMode('login')}
                  >
                    {t('login.loginButton')}
                  </button>
                </>
              )}
            </p>
          </div>

          {/* 입력 폼 */}
          <div className="popup-login-form">
            {isLogin ? (
              <>
                <div className="popup-login-input-wrapper">
                  <input
                    type="text"
                    className="popup-login-input"
                    placeholder={t('login.identifier')}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </div>
                <div className="popup-login-input-wrapper">
                  <input
                    type="password"
                    className="popup-login-input"
                    placeholder={t('login.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </div>
                <div className="popup-login-input-wrapper">
                  <a
                    href="/find-password"
                    className="popup-login-forgot"
                    onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/find-password'; }}
                  >
                    {t('login.forgotIdPassword')}
                  </a>
                </div>
              </>
            ) : (
              <>
                <div className="popup-login-input-wrapper">
                  <input
                    type="text"
                    className="popup-login-input"
                    placeholder={t('signup.nicknamePlaceholder')}
                    value={signupNickname}
                    onChange={(e) => setSignupNickname(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </div>
                <div className="popup-login-input-wrapper">
                  <input
                    type="email"
                    className="popup-login-input"
                    placeholder={t('signup.emailPlaceholder')}
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </div>
                <div className="popup-login-input-wrapper">
                  <input
                    type="password"
                    className="popup-login-input"
                    placeholder={t('signup.passwordPlaceholder')}
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </div>
                <div className="popup-login-input-wrapper">
                  <input
                    type="password"
                    className="popup-login-input"
                    placeholder={t('signup.confirmPlaceholder')}
                    value={signupConfirm}
                    onChange={(e) => setSignupConfirm(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </div>
              </>
            )}

            {message && (
              <div className={`popup-login-message ${message.includes('성공') || message.includes('success') || message.includes('Success') ? 'success' : 'error'}`}>
                {message}
              </div>
            )}

            <button
              className="btn-rtg btn-primary"
              onClick={isLogin ? handleLogin : handleSignup}
              disabled={loading}
            >
              {loading
                ? (isLogin ? t('login.loggingIn') : t('signup.registering'))
                : (isLogin ? t('login.loginButton') : t('signup.registerButton'))}
            </button>
          </div>

          {/* 구분선 */}
          <div className="popup-login-divider">
            <span className="popup-login-divider-text">{t('login.or')}</span>
          </div>

          {/* 소셜 버튼 (로그인/가입 공용) */}
          <div className="popup-login-social">
            <SocialLoginButtons
              onSuccess={handleSocialLoginSuccess}
              onError={handleSocialLoginError}
            />
          </div>

          {/* 푸터 */}
          <div className="popup-login-footer">
            <div className="popup-login-footer-links">
              <a
                href="/terms-of-service"
                className="popup-login-footer-link"
                onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/terms-of-service'; }}
              >
                {t('login.terms')}
              </a>
              <a
                href="/privacy-policy"
                className="popup-login-footer-link"
                onClick={(e) => { e.preventDefault(); onClose(); window.location.href = '/privacy-policy'; }}
              >
                {t('login.privacy')}
              </a>
            </div>
            <p className="popup-login-copyright">{t('login.copyright')}</p>
          </div>
        </div>

        <div className="popup-login-sidebar" />
      </div>
    </div>
  );
};

export default PopupLogin;
